<script setup lang="ts">
/**
 * LiveRegion Component
 * 
 * Provides an accessible way to announce dynamic content changes to screen readers.
 * Uses aria-live regions for both polite and assertive announcements.
 * 
 * WCAG 4.1.3 - Status Messages
 */

import { ref } from 'vue'

// Expose methods to announce messages globally
const politeMessage = ref('')
const assertiveMessage = ref('')

// Global announce function
function announcePolite(message: string) {
  politeMessage.value = ''
  // Small delay to ensure screen reader picks up the change
  setTimeout(() => {
    politeMessage.value = message
  }, 100)
}

function announceAssertive(message: string) {
  assertiveMessage.value = ''
  setTimeout(() => {
    assertiveMessage.value = message
  }, 100)
}

// Expose to parent components
defineExpose({
  announcePolite,
  announceAssertive
})

// Make available globally via provide/inject or event bus
if (typeof window !== 'undefined') {
  (window as any).__announcePolite = announcePolite;
  (window as any).__announceAssertive = announceAssertive;
}
</script>

<template>
  <!-- Polite announcements - don't interrupt current speech -->
  <div
    id="live-region-polite"
    class="sr-only"
    aria-live="polite"
    aria-atomic="true"
    role="status"
  >
    {{ politeMessage }}
  </div>
  
  <!-- Assertive announcements - interrupt immediately (use sparingly) -->
  <div
    id="live-region-assertive"
    class="sr-only"
    aria-live="assertive"
    aria-atomic="true"
    role="alert"
  >
    {{ assertiveMessage }}
  </div>
  
  <!-- Route change announcer -->
  <div
    id="route-announcer"
    class="sr-only"
    aria-live="polite"
    aria-atomic="true"
  >
    <!-- Populated by router after navigation -->
  </div>
</template>

<style scoped>
/* Visually hidden but accessible to screen readers */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
